import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { BRAND, STATS, HERO_IMG } from "@/data/site";
import { lenisRef } from "@/lib/lenis";
import { Counter } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const STAT_KEYS = ["stats.services", "stats.support", "stats.years", "stats.uptime"];

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useI18n();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 -z-20"
      >
        <img
          src={HERO_IMG}
          alt="Rețea neuronală de date"
          className="h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/60 via-ink-900/75 to-ink-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/45 to-transparent" />
      </motion.div>

      {/* Animated aurora blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="aurora-blob animate-drift left-[-8%] top-[-10%] h-[42vw] w-[42vw] bg-cyan-neon/25" />
        <div className="aurora-blob animate-drift-slow right-[-10%] top-[10%] h-[38vw] w-[38vw] bg-[#7C5CFC]/22" />
        <div className="aurora-blob animate-drift bottom-[-15%] left-[30%] h-[34vw] w-[34vw] bg-[#0055FF]/18" />
      </div>
      <div className="absolute inset-0 -z-10 grid-lines opacity-60" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="mx-auto w-full max-w-7xl px-5 pt-32 pb-16 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-7 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-neon shadow-[0_0_12px_#00E5FF]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/70">
            {BRAND.name} — {BRAND.tagline}
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
          <Line delay={0.35}>{t("hero.l1")}</Line>
          <Line delay={0.5}>
            {t("hero.l2")}{" "}
            <span className="text-aurora text-aurora-glow">{t("hero.kw")}</span>
          </Line>
          <Line delay={0.65}>{t("hero.l3")}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
          className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/60 sm:text-lg"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col gap-3 sm:flex-row"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => {
              const lenis = lenisRef.current;
              if (lenis) lenis.scrollTo("#contact", { offset: -60, duration: 1.2 });
              else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-shine group inline-flex items-center justify-center gap-2 bg-cyan-neon px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black shadow-[0_0_30px_rgba(0,229,255,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-white hover:shadow-[0_0_45px_rgba(0,229,255,0.55)]"
          >
            <MessageCircle size={17} />
            {t("hero.cta1")}
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => {
              const lenis = lenisRef.current;
              if (lenis) lenis.scrollTo("#servicii", { offset: -60, duration: 1.2 });
              else document.querySelector("#servicii")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group inline-flex items-center justify-center gap-2 rounded-none border border-white/20 bg-white/5 px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-white backdrop-blur-md transition-colors duration-300 hover:border-cyan-neon hover:text-cyan-neon"
          >
            {t("hero.cta2")}
            <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
          className="mt-16 grid max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 backdrop-blur-md sm:grid-cols-4"
        >
          {STATS.map((s, idx) => (
            <div key={s.label} className="bg-ink-900/70 px-5 py-6">
              <div className="font-display text-3xl font-extrabold text-cyan-neon">
                <Counter value={s.value} />
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/45">
                {t(STAT_KEYS[idx])}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
        scroll ↓
      </div>
    </section>
  );
};
